/**
 * CredentialIcon Component
 * Reusable component for rendering credential type icons consistently
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { getCredentialDefinition } from '@/utils/credentialDefinitions';
import type { LucideIcon } from 'lucide-react';

interface CredentialIconProps {
  /** Credential type (e.g., 'sql_server', 'openai') */
  type: string;

  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';

  /** Show background container */
  showBackground?: boolean;

  /** Additional CSS classes */
  className?: string;
}

const sizeMap = {
  sm: { icon: 16, container: 'p-1.5' },
  md: { icon: 20, container: 'p-2' },
  lg: { icon: 24, container: 'p-2.5' },
  xl: { icon: 32, container: 'p-3' },
};

/**
 * Renders a credential type icon with optional background styling
 *
 * @example
 * // Simple icon
 * <CredentialIcon type="sql_server" size="md" />
 *
 * @example
 * // Icon with background
 * <CredentialIcon type="openai" size="lg" showBackground />
 */
export const CredentialIcon: React.FC<CredentialIconProps> = ({
  type,
  size = 'md',
  showBackground = false,
  className,
}) => {
  const definition = getCredentialDefinition(type);
  const { icon: Icon, iconType, iconColor, bgColor, color } = definition;
  const { icon: iconSize, container: containerPadding } = sizeMap[size];

  const renderIcon = () => {
    if (iconType === 'simple-icon') {
      // Simple Icons component from @icons-pack/react-simple-icons
      return (
        <Icon
          size={iconSize}
          color={iconColor || color}
          className="flex-shrink-0"
          aria-hidden="true"
        />
      );
    }

    if (iconType === 'microsoft-icon') {
      // Microsoft custom SVG components
      return (
        <Icon
          size={iconSize}
          className="flex-shrink-0"
          aria-hidden="true"
        />
      );
    }

    if (iconType === 'integration-icon') {
      // Custom third-party integration SVG icons (e.g., Asana)
      return (
        <Icon
          size={iconSize}
          className="flex-shrink-0"
          aria-hidden="true"
        />
      );
    }

    // Lucide icon component
    const LucideIconComponent = Icon as LucideIcon;
    return (
      <LucideIconComponent
        className="flex-shrink-0"
        style={{
          width: iconSize,
          height: iconSize,
          color: color,
        }}
        aria-hidden="true"
      />
    );
  };

  if (showBackground) {
    return (
      <div
        className={cn(
          'rounded-lg flex items-center justify-center',
          containerPadding,
          className
        )}
        style={{ backgroundColor: bgColor }}
      >
        {renderIcon()}
      </div>
    );
  }

  return (
    <span className={cn('inline-flex items-center justify-center', className)}>
      {renderIcon()}
    </span>
  );
};

export default CredentialIcon;
